import { useSearchIcd10Codes, type Icd10Code } from "@/clinical-codes/useSearchIcd10Codes";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { DragDropContext, Draggable, Droppable, type DropResult } from "@hello-pangea/dnd";
import { useState } from "react";
import type { ExpectedIcd10Code } from "./useMedicalTextCodes";

interface ComorbiditiesSectionProps {
  selectedCoMorbidities: ExpectedIcd10Code[];
  onCoMorbiditiesChange: (comorbidities: ExpectedIcd10Code[]) => void;
  onToggleOptional: (id: string) => void;
  onFormChange: () => void;
}

export function ComorbiditiesSection({
  selectedCoMorbidities,
  onCoMorbiditiesChange,
  onToggleOptional,
  onFormChange,
}: ComorbiditiesSectionProps) {
  const [diagnosesSearchTerm, setDiagnosesSearchTerm] = useState("");
  const { codes: diagnosisCodes } = useSearchIcd10Codes(diagnosesSearchTerm);

  const addComorbidity = (code: Icd10Code) => {
    if (!selectedCoMorbidities.find((c) => c.code_id === code.id)) {
      const newCode: ExpectedIcd10Code = {
        id: crypto.randomUUID(),
        code_id: code.id,
        is_optional: false,
        medical_text_example_id: "to_be_replaced",
        order_index: selectedCoMorbidities.length,
        icd_10_code: code,
      };
      onCoMorbiditiesChange([...selectedCoMorbidities, newCode]);
      onFormChange();
    }
  };

  const removeComorbidity = (_id: string, index: number) => {
    const newCoMorbidities = [...selectedCoMorbidities];
    newCoMorbidities.splice(index, 1);
    onCoMorbiditiesChange(newCoMorbidities);
    onFormChange();
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const { source, destination } = result;
    const items = Array.from(selectedCoMorbidities);
    const [removed] = items.splice(source.index, 1);
    items.splice(destination.index, 0, removed);
    onCoMorbiditiesChange(items);
    onFormChange();
  };

  return (
    <div className="space-y-2 w-full">
      <Label>Co-morbidities (ICD-10)</Label>
      <div className="w-full px-1 mb-2 relative">
        <input
          type="text"
          className="w-full border rounded px-2 py-1"
          placeholder="Search co-morbidities..."
          value={diagnosesSearchTerm}
          onChange={(e) => setDiagnosesSearchTerm(e.target.value)}
        />
        {diagnosesSearchTerm && (
          <div className="absolute left-0 right-0 bg-white border rounded shadow mt-1 max-h-40 overflow-y-auto z-50">
            {diagnosisCodes
              ?.filter(
                (code) =>
                  code.code.toLowerCase().includes(diagnosesSearchTerm.toLowerCase()) ||
                  code.description.toLowerCase().includes(diagnosesSearchTerm.toLowerCase()),
              )
              .filter((code) => !selectedCoMorbidities.some((c) => c.code_id === code.id))
              .slice(0, 10)
              .map((code) => (
                <div
                  key={code.id}
                  className="px-2 py-1 hover:bg-gray-100 cursor-pointer"
                  onClick={() => {
                    addComorbidity(code);
                    setDiagnosesSearchTerm("");
                  }}
                >
                  {code.code} - {code.description}
                </div>
              ))}
          </div>
        )}
      </div>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="comorbidities-droppable" type="comorbidities">
          {(provided) => (
            <ul
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="bg-gray-50 rounded border min-h-[40px] p-2"
            >
              {selectedCoMorbidities.map((code, idx) => (
                <Draggable key={`${code.id}-${idx}`} draggableId={`${code.id}-${idx}`} index={idx}>
                  {(provided, snapshot) => (
                    <li
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`flex items-center justify-between p-3 mb-1 rounded shadow-sm ${
                        snapshot.isDragging ? "ring-2 ring-blue-400" : ""
                      } ${code.is_optional ? "bg-green-50" : "bg-white"}`}
                    >
                      <span {...provided.dragHandleProps} className="cursor-move mr-2">
                        ☰
                      </span>
                      <div className="flex items-start justify-start gap-2 flex-1">
                        <span>
                          {code.icd_10_code.code} - {code.icd_10_code.description}
                          {code.is_optional && <span className="text-green-600 text-sm ml-2">(Optional)</span>}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="flex items-center space-x-1">
                          <Label htmlFor={`comorbid-optional-${idx}`} className="text-xs">
                            Optional
                          </Label>
                          <Switch
                            id={`comorbid-optional-${idx}`}
                            checked={code.is_optional}
                            onCheckedChange={() => onToggleOptional(code.id)}
                          />
                        </div>
                        <button
                          type="button"
                          className="ml-2 text-red-500 hover:text-red-700"
                          onClick={() => removeComorbidity(code.id, idx)}
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
}
